// Searchable city picker for the AMEEX parcel forms. Loads the static
// catalogue scraped from the AMEEX dashboard (web/src/data/ameex-cities.json)
// and lets the admin type to filter — typing "casa" picks Casablanca (id 129),
// "rab" picks Rabat (id 49), etc. The selected city's numeric id is what gets
// sent to AMEEX's API; the name+code are just for display.

import { useMemo, useRef, useState, useEffect } from 'react';
import cities from '@/data/ameex-cities.json';
import { useT } from '@/lib/i18n';

type City = {
  id: number;
  name: string;
  code: string | null;
  zone: string | null;
  d_fees: number | null;
};

const CITY_LIST = cities as unknown as City[];

export function AmeexCitySelect({
  value,
  onChange,
  placeholder,
  required,
}: {
  /** Selected city id as a string (so it composes with the existing form state). */
  value: string;
  onChange: (next: { id: string; name: string }) => void;
  placeholder?: string;
  required?: boolean;
}) {
  const { t } = useT();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Whenever the parent's value changes from outside (e.g. order reload),
  // sync the query input to show the city name instead of the bare id.
  useEffect(() => {
    if (!value) { setQuery(''); return; }
    const idNum = parseInt(value, 10);
    const found = CITY_LIST.find((c) => c.id === idNum);
    if (found) setQuery(`${found.name}${found.code ? ` (${found.code})` : ''}`);
  }, [value]);

  // Filter by name OR code, case-insensitive, French-accent-insensitive.
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return CITY_LIST.slice(0, 12);
    const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    const nq = norm(q);
    return CITY_LIST.filter((c) =>
      norm(c.name).includes(nq) || (c.code && c.code.toLowerCase().includes(q)),
    ).slice(0, 50);
  }, [query]);

  // Close the dropdown when clicking outside the wrapper.
  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  function pick(c: City) {
    onChange({ id: String(c.id), name: c.name });
    setQuery(`${c.name}${c.code ? ` (${c.code})` : ''}`);
    setOpen(false);
  }

  return (
    <div ref={wrapRef} className="relative">
      <input
        type="text"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); setHighlight(0); }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (!open) return;
          if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => Math.min(matches.length - 1, h + 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => Math.max(0, h - 1)); }
          else if (e.key === 'Enter' && matches[highlight]) { e.preventDefault(); pick(matches[highlight]); }
          else if (e.key === 'Escape') setOpen(false);
        }}
        required={required}
        placeholder={placeholder ?? t('Tapez pour rechercher (ex. Casa, Rabat…)')}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
        autoComplete="off"
      />
      {open && matches.length > 0 && (
        <ul
          role="listbox"
          className="absolute z-20 left-0 right-0 mt-1 max-h-60 overflow-auto bg-white border border-gray-200 rounded-lg shadow-lg text-sm"
        >
          {matches.map((c, i) => (
            <li
              key={c.id}
              role="option"
              aria-selected={i === highlight}
              onMouseDown={(e) => { e.preventDefault(); pick(c); }}
              onMouseEnter={() => setHighlight(i)}
              className={`flex items-baseline justify-between gap-3 px-3 py-2 cursor-pointer ${i === highlight ? 'bg-gray-50' : ''}`}
            >
              <span className={c.zone == null ? 'text-gray-400' : 'text-gray-800'}>
                {c.name}
                {c.code && <span className="text-gray-400 text-xs ml-1.5">({c.code})</span>}
                {/* No delivery zone = AMEEX does not serve it; creating a parcel
                    there fails. Flagged here so it is caught before the attempt
                    rather than after (see #D382 / OUED LAOU, 2026-07-29). */}
                {c.zone == null && (
                  <span
                    className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded"
                    style={{ background: '#FEE2E2', color: '#B91C1C' }}
                  >
                    {t('AMEEX ne livre pas ici')}
                  </span>
                )}
              </span>
              <span className="text-xs text-gray-400 font-mono shrink-0">
                {c.d_fees != null ? `${c.d_fees} MAD` : ''}
                <span className="ml-2 opacity-60">#{c.id}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
      {open && matches.length === 0 && query.trim() && (
        <div className="absolute z-20 left-0 right-0 mt-1 px-3 py-2 bg-white border border-gray-200 rounded-lg shadow-lg text-xs text-gray-500">
          {t('Aucune ville trouvée.')}
        </div>
      )}
    </div>
  );
}
